import type { CardRef } from "@pra/shared/battle-log";
import type { GameSummary } from "@pra/shared/contracts";
import type * as React from "react";
import { Badge } from "../design-system/components";
import { useCatalog } from "../lib/catalog";

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="pr-empty">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function ResultBadge({ game }: { game: Pick<GameSummary, "result"> }) {
  if (game.result === "win") return <Badge variant="success">Win</Badge>;
  if (game.result === "loss") return <Badge variant="error">Loss</Badge>;
  return <Badge variant="outline">Unknown</Badge>;
}

export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="rk-pagehead">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="rk-actions">{actions}</div>}
    </div>
  );
}

/** Renders card refs (ids or names) as a compact two-column list, names resolved via the catalog. */
export function CardList({
  cards,
  emptyText = "No cards",
}: {
  cards: CardRef[] | undefined;
  emptyText?: string;
}) {
  const { name, print } = useCatalog();
  if (!cards || cards.length === 0)
    return (
      <p className="pr-muted" style={{ fontSize: 14 }}>
        {emptyText}
      </p>
    );
  const sorted = [...cards].sort((a, b) => name(a).localeCompare(name(b)));
  return (
    <ul className="pr-decklist" style={{ margin: 0, padding: 0, listStyle: "none" }}>
      {sorted.map((card) => (
        <li key={card.cardId ?? `${card.name}|${card.set}|${card.number}`}>
          <span className="count">{card.count}×</span>
          <span>
            {name(card)}
            {print(card) ? <span className="pr-muted"> {print(card)}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return <div className="pr-alert pr-alert--error">{message}</div>;
}
